import Debug from 'debug';
import React, { useRef } from 'react';
import { Formik, FormikErrors } from 'formik';
import { useDispatch } from 'react-redux';
import { CompoundResponse, Outcome, Primitive } from 'gossamer-unified';
import { notify } from '../ancillary/NotifyManager';
import { ParallelLoad } from '../data/ParallelLoad';
import { fetchApi } from '../data/fetchApi';
import { makeResetCache } from '../data/ReduxGCacheMiddleware';
import { FormButtons } from './FormButtons';
import {
  addRowsToPayload,
  AllValues,
  getAllInitialValues,
  Initialization,
  makeCompoundPayload,
  mapInitializerToResponse,
  notifyResponse,
  validateAll,
} from './Utils';
import { Section } from '../sections/Section';

const debug = Debug('gfe/fo/FormManager');

export type ChangeFunc = (name: string, value: Primitive) => void;

export interface FormManagerProps {
  children?: React.ReactNode;
  compoundUrl?: string;
  initialization: Initialization;
  handleCancel?: () => void;
  handleInitialise?: (values: AllValues) => void;
  handlePreSubmit?: (values: AllValues) => void;
  handleSuccess?: (
    resp: CompoundResponse,
    resetCache: (entityId: string, key?: string) => void,
    values: AllValues,
  ) => void;
  handleSubmit?: (values: AllValues) => Promise<void>;
  outcomes?: Outcome[];
  render?: (props: FormManagerRenderProps) => React.JSX.Element;
  validate?: (values: AllValues) => FormikErrors<any> | Promise<any>;
}

export const FormManager = (props: FormManagerProps): React.JSX.Element => {
  if ((props.compoundUrl && props.handleSubmit) || (!props.compoundUrl && !props.handleSubmit)) {
    throw new Error(`please supply one of either compoundUrl or handleSubmit as props, but not both`);
  }
  return (
    <ParallelLoad
      ignoreErrors={true}
      responses={Object.values(props.initialization)
        .map(mapInitializerToResponse)
        .filter((response) => !!response)}
    >
      <FormManagerOuter {...props} />
    </ParallelLoad>
  );
};

const FormManagerOuter = (props: FormManagerProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const submitted = useRef<boolean>(false);
  const initialValues = getAllInitialValues(props.initialization);
  debug('FormManagerOuter', { initialValues });
  if (props.handleInitialise) {
    props.handleInitialise(initialValues);
  }
  const validate = (values: any) => {
    const out = validateAll(props.initialization, values);
    debug('validation:', out);
    return out;
  };

  const handleSubmit = async (values: any) => {
    if (submitted.current) {
      throw new Error(`already submitted`);
    }
    if (!!validate(values)) {
      throw new Error(`validation error`);
    }
    submitted.current = true;
    if (props.handlePreSubmit) {
      props.handlePreSubmit(values);
    }
    debug(values);
    const payload = makeCompoundPayload(values._outcome);
    addRowsToPayload(props.initialization, payload, values);
    debug(payload);
    try {
      if (!props.compoundUrl) {
        throw new Error('compoundUrl not supplied');
      }
      const resp: CompoundResponse = await fetchApi('POST', props.compoundUrl, payload);
      // reset cache
      const resetCache = makeResetCache(dispatch);
      payload.rows.forEach((row) => {
        resetCache(row.entityId, row.key);
      });
      notifyResponse(resp);
      if (props.handleSuccess) {
        props.handleSuccess(resp, resetCache, values);
      }
    } catch (error) {
      const err = error as { statusCode?: number; message?: string };
      console.error(err.statusCode, err.message);
      notify([err.message || 'save failed', 'danger']);
      submitted.current = false;
      // return { [FORM_ERROR]: msg };
    }
  };
  const Render = props.render || FormContentDefault;
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={props.handleSubmit || handleSubmit}
      validate={props.validate || validate}
    >
      {(innerProps) => (
        <form onSubmit={innerProps.handleSubmit}>
          {/* {innerProps.submitError && <Alert variant="danger">{innerProps.submitError}</Alert>} */}
          <Render {...props} {...innerProps} />
        </form>
      )}
    </Formik>
  );
};

export interface FormManagerRenderProps {
  children?: React.ReactNode;
  handleCancel?: () => void;
  handleSubmit: () => void;
  outcomes?: Outcome[];
  // submitError: any;
  isSubmitting: boolean;
  isValid: boolean;
  // errors: { [field: string]: string };
  setFieldError: (field: string, errorMsg: string) => void;
  setFieldValue: (
    field: string,
    value: React.SetStateAction<Primitive>,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<any>>;
  values: AllValues;
}

export const FormContentDefault = (props: FormManagerRenderProps) => {
  return (
    <>
      {props.children || null}
      <Section>
        <FormButtons
          handleCancel={props.handleCancel}
          handleSubmit={(outcomeId: string) => {
            debug(`submitting`, outcomeId);
            props.setFieldValue('_outcome', outcomeId);
            props.handleSubmit();
          }}
          submitting={props.isSubmitting}
          outcomes={props.outcomes}
        />
      </Section>
    </>
  );
};
